import memoize from "@-/com.npmjs.memoize";
import {
  CHINA_MAP_LABEL_FONT_SIZE,
  CHINA_MAP_LABEL_GLYPH_HALF_SIZE,
  getChinaMapLabelLineOffsets,
  getChinaMapLabelOffsetUnit,
} from "@-/dev.improve.haniverse.data.china-map";
import {
  doesChinaMapBboxIntersectRings,
  doesChinaMapSegmentIntersectBbox,
  doesChinaMapSegmentIntersectRings,
  doChinaMapBboxesIntersect,
  doChinaMapSegmentsIntersect,
  getChinaMapPlaceRings,
  isPointInChinaMapRings,
  type ChinaMapBbox,
  type ChinaMapPoint,
} from "#china-map-geometry";
import type { ChinaMapLabel, ChinaMapProvinceView } from "#china-map-places";

const getLineOffsets = memoize(getChinaMapLabelLineOffsets);
const GAPS = [6, 12, 20, 32, 48, 64];
const SHIFTS = [0, -12, 12, -24, 24, -40, 40, -64, 64];
/** A callout must remain nearby and cannot weave across unrelated territory. */
const MAX_LEADER_LENGTH = 100;

/** Padded glyph boxes in map units, including scattered layouts whose positions stay fixed. */
export function getChinaMapLabelBboxes(label: ChinaMapLabel, scale: number): ChinaMapBbox[] {
  const em = label.fontSize / scale;
  const half = em * CHINA_MAP_LABEL_GLYPH_HALF_SIZE + 2 / scale;
  const unit = getChinaMapLabelOffsetUnit(label.layout, em);
  return label.layout.offsets.map(([dx, dy]) => {
    const x = label.layout.anchor[0] + dx * unit;
    const y = label.layout.anchor[1] + dy * unit;
    return [x - half, y - half, x + half, y + half];
  });
}

/**
 * Uses only offline water-facing anchors. Every candidate is checked against exact land outlines,
 * every existing glyph and leader, and the unobscured viewport. There is no random search or
 * screen-frame work: a settled camera chooses a nearby valid callout deterministically.
 * If no safe position exists, the existing interior fallback is retained.
 */
export function placeChinaMapCoastalLabels(
  labels: readonly ChinaMapLabel[],
  {
    alwaysLabel,
    minFontSize,
    provinces,
    scale,
    visibleBbox,
  }: {
    alwaysLabel: boolean;
    minFontSize: number;
    provinces: readonly ChinaMapProvinceView[];
    scale: number;
    visibleBbox?: ChinaMapBbox;
  },
): ChinaMapLabel[] {
  if (!Number.isFinite(scale) || scale <= 0) {
    return [];
  }
  const land = provinces.map(({ place }) => ({
    bbox: place.bbox,
    rings: getChinaMapPlaceRings(place),
  }));
  const fallback = (label: ChinaMapLabel): ChinaMapLabel | null =>
    label.fontSize === 0
      ? null
      : alwaysLabel
        ? { ...label, fontSize: Math.max(minFontSize, label.fontSize) }
        : label.fontSize >= minFontSize
          ? label
          : null;
  const placed = new Map<number, ChinaMapLabel>();
  const occupied: { adcode: number; box: ChinaMapBbox }[] = [];
  const leaders: { from: ChinaMapPoint; to: ChinaMapPoint }[] = [];
  for (const label of labels) {
    const inside = fallback(label);
    if (inside !== null) {
      placed.set(label.view.place.adcode, inside);
      occupied.push(
        ...getChinaMapLabelBboxes(inside, scale).map((box) => ({
          adcode: label.view.place.adcode,
          box,
        })),
      );
    }
  }
  const candidates = labels
    .filter(
      ({ fontSize, view }) =>
        fontSize < CHINA_MAP_LABEL_FONT_SIZE && (view.place.coast?.length ?? 0) > 0,
    )
    .sort(
      (a, b) =>
        (a.view.place.coast?.length ?? 0) - (b.view.place.coast?.length ?? 0) ||
        a.view.place.adcode - b.view.place.adcode,
    );
  const preferredCoasts = candidates.flatMap((label) => {
    const coast = label.view.place.coast
      ?.slice()
      .sort(
        (a, b) =>
          Math.hypot(a[0] - label.layout.anchor[0], a[1] - label.layout.anchor[1]) -
          Math.hypot(b[0] - label.layout.anchor[0], b[1] - label.layout.anchor[1]),
      )[0];
    return coast === undefined ? [] : [{ coast, label }];
  });
  /** Fan neighboring names out in shoreline order before testing exact collisions. A greedy
   * nearest placement can otherwise fence in the next tiny city with the first city's text. */
  const getPreferredPlacement = (adcode: number, x: number, y: number, nx: number, ny: number) => {
    const tx = -ny;
    const ty = nx;
    const peers = preferredCoasts
      .filter(
        ({ coast }) =>
          coast[2] * nx + coast[3] * ny > 0.95 &&
          Math.abs((coast[0] - x) * nx + (coast[1] - y) * ny) * scale < 24 &&
          Math.abs((coast[0] - x) * tx + (coast[1] - y) * ty) * scale < 100,
      )
      .sort((a, b) => (a.coast[0] - b.coast[0]) * tx + (a.coast[1] - b.coast[1]) * ty);
    if (peers.length < 2) {
      return { gap: 6, shift: 0 };
    }
    const spans = peers.map(
      ({ label }) =>
        Math.abs(tx) *
          (Array.from(label.view.displayName, (character) => character).length *
            CHINA_MAP_LABEL_FONT_SIZE +
            6) +
        Math.abs(ty) * (CHINA_MAP_LABEL_FONT_SIZE + 6),
    );
    const total = spans.reduce((sum, span) => sum + span, 0);
    const first = peers[0]?.coast ?? [x, y];
    const last = peers.at(-1)?.coast ?? [x, y];
    const shoreSpan =
      ((last[0] ?? x) - (first[0] ?? x)) * tx * scale +
      ((last[1] ?? y) - (first[1] ?? y)) * ty * scale;
    // Dense labels need room to fan out before their text begins, or one text box can cut off
    // the next city's incoming line. Isolated coastal names keep their short six-pixel leaders.
    const gap = Math.min(64, Math.max(6, (total - shoreSpan) / 4));
    let center =
      peers.reduce((sum, { coast }) => sum + (coast[0] * tx + coast[1] * ty) * scale, 0) /
      peers.length;
    if (visibleBbox !== undefined) {
      const projections = [
        visibleBbox[0] * tx + visibleBbox[1] * ty,
        visibleBbox[0] * tx + visibleBbox[3] * ty,
        visibleBbox[2] * tx + visibleBbox[1] * ty,
        visibleBbox[2] * tx + visibleBbox[3] * ty,
      ].map((projection) => projection * scale);
      const min = Math.min(...projections) + total / 2;
      const max = Math.max(...projections) - total / 2;
      if (min <= max) {
        center = Math.min(max, Math.max(min, center));
      }
    }
    let offset = center - total / 2;
    for (const [index, peer] of peers.entries()) {
      const span = spans[index] ?? 0;
      if (peer.label.view.place.adcode === adcode) {
        return { gap, shift: offset + span / 2 - (x * tx + y * ty) * scale };
      }
      offset += span;
    }
    return { gap: 6, shift: 0 };
  };
  let remaining = candidates;
  // A neighbor's successful callout frees the overflowing fallback it previously occupied.
  // Retry only unresolved names, at most once per successful placement; no unbounded solver.
  for (let pass = 0; pass < candidates.length && remaining.length > 0; pass += 1) {
    const retry: ChinaMapLabel[] = [];
    let progressed = false;
    for (const label of remaining) {
      const { view } = label;
      const adcode = view.place.adcode;
      const count = Array.from(view.displayName, (character) => character).length;
      if (count === 0) {
        continue;
      }
      const halfWidth = ((count * CHINA_MAP_LABEL_FONT_SIZE) / 2 + 2.25) / scale;
      const halfHeight = (CHINA_MAP_LABEL_FONT_SIZE / 2 + 2.25) / scale;
      const rings = getChinaMapPlaceRings(view.place);
      let best: ChinaMapLabel | null = null;
      const options: {
        anchor: ChinaMapPoint;
        box: ChinaMapBbox;
        cost: number;
        from: ChinaMapPoint;
        start: ChinaMapPoint;
        to: ChinaMapPoint;
      }[] = [];
      const coasts = [...(view.place.coast ?? [])]
        .sort(
          (a, b) =>
            Math.hypot(a[0] - label.layout.anchor[0], a[1] - label.layout.anchor[1]) -
            Math.hypot(b[0] - label.layout.anchor[0], b[1] - label.layout.anchor[1]),
        )
        .slice(0, 32);
      for (const [x, y, nx, ny] of coasts) {
        if (
          ![x, y, nx, ny].every((value) => Number.isFinite(value)) ||
          Math.abs(Math.hypot(nx, ny) - 1) > 0.01
        ) {
          continue;
        }
        if (
          visibleBbox !== undefined &&
          (x < visibleBbox[0] || y < visibleBbox[1] || x > visibleBbox[2] || y > visibleBbox[3])
        ) {
          continue;
        }
        // A discarded sliver/islet must never supply a leader. The starting point lies on the
        // actual rendered boundary, with land just inside and water just outside.
        const epsilon = 0.00001 / scale;
        if (!isPointInChinaMapRings(rings, x - nx * epsilon, y - ny * epsilon)) {
          continue;
        }
        const from: ChinaMapPoint = [x, y];
        const start: ChinaMapPoint = [x + nx * epsilon, y + ny * epsilon];
        const { gap: fanGap, shift: fanShift } = getPreferredPlacement(adcode, x, y, nx, ny);
        for (const gap of GAPS) {
          const distance = Math.abs(nx) * halfWidth + Math.abs(ny) * halfHeight + gap / scale;
          for (const shift of [...SHIFTS, fanShift, fanShift - 6, fanShift + 6]) {
            const anchor: ChinaMapPoint = [
              x + nx * distance - (ny * shift) / scale,
              y + ny * distance + (nx * shift) / scale,
            ];
            const box: ChinaMapBbox = [
              anchor[0] - halfWidth,
              anchor[1] - halfHeight,
              anchor[0] + halfWidth,
              anchor[1] + halfHeight,
            ];
            if (
              visibleBbox !== undefined &&
              (box[0] < visibleBbox[0] ||
                box[1] < visibleBbox[1] ||
                box[2] > visibleBbox[2] ||
                box[3] > visibleBbox[3])
            ) {
              continue;
            }
            const dx = anchor[0] - x;
            const dy = anchor[1] - y;
            const edge = Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);
            if (edge <= 1) {
              continue;
            }
            const to: ChinaMapPoint = [anchor[0] - dx / edge, anchor[1] - dy / edge];
            const length = Math.hypot(to[0] - x, to[1] - y) * scale;
            const cost =
              length +
              Math.abs(shift) * 0.15 +
              Math.abs(shift - fanShift) * 2 +
              Math.max(0, fanGap - gap) * 3 +
              Math.hypot(x - label.layout.anchor[0], y - label.layout.anchor[1]) * scale * 0.1;
            if (length > MAX_LEADER_LENGTH) {
              continue;
            }
            options.push({ anchor, box, cost, from, start, to });
          }
        }
      }
      // Cheaply rank first, then stop at the first collision-free placement. The same winner
      // results without doing expensive exact outline checks on every inferior candidate.
      options.sort((a, b) => a.cost - b.cost);
      for (const { anchor, box, from, start, to } of options) {
        const lineBox: ChinaMapBbox = [
          Math.min(start[0], to[0]),
          Math.min(start[1], to[1]),
          Math.max(start[0], to[0]),
          Math.max(start[1], to[1]),
        ];
        if (
          occupied.some(
            (other) =>
              other.adcode !== adcode &&
              (doChinaMapBboxesIntersect(box, other.box) ||
                doesChinaMapSegmentIntersectBbox(from, to, other.box)),
          )
        ) {
          continue;
        }
        if (
          leaders.some(
            (other) =>
              doesChinaMapSegmentIntersectBbox(other.from, other.to, box) ||
              doChinaMapSegmentsIntersect(from, to, other.from, other.to),
          )
        ) {
          continue;
        }
        if (
          land.some(
            (other) =>
              (doChinaMapBboxesIntersect(box, other.bbox) &&
                doesChinaMapBboxIntersectRings(other.rings, box)) ||
              (doChinaMapBboxesIntersect(lineBox, other.bbox) &&
                doesChinaMapSegmentIntersectRings(other.rings, start, to)),
          )
        ) {
          continue;
        }
        best = {
          fontSize: CHINA_MAP_LABEL_FONT_SIZE,
          layout: {
            anchor,
            em: CHINA_MAP_LABEL_FONT_SIZE / scale,
            offsets: getLineOffsets(count),
          },
          leader: { from, to },
          view,
        };
        break;
      }
      if (best !== null && best.leader !== undefined) {
        placed.set(adcode, best);
        for (let index = occupied.length - 1; index >= 0; index -= 1) {
          if (occupied[index]?.adcode === adcode) {
            occupied.splice(index, 1);
          }
        }
        // A single box reserves the spaces between glyphs as well as their ink.
        occupied.push({
          adcode,
          box: [
            best.layout.anchor[0] - halfWidth,
            best.layout.anchor[1] - halfHeight,
            best.layout.anchor[0] + halfWidth,
            best.layout.anchor[1] + halfHeight,
          ],
        });
        leaders.push(best.leader);
        progressed = true;
      } else {
        retry.push(label);
      }
    }
    if (!progressed) {
      break;
    }
    remaining = retry;
  }
  return labels.flatMap(({ view }) => {
    const result = placed.get(view.place.adcode);
    return result === undefined ? [] : [result];
  });
}
